// Top US contractor markets — fuel for the programmatic /<trade>-crm/<city>
// pages that target "[trade] CRM [city]" long-tail searches.
//
// Picked for a mix of (a) sheer construction-permit volume — Sun Belt
// metros that lead the country in residential and commercial builds —
// and (b) HVAC / plumbing / electrical service-call volume in older
// Northeast and Midwest housing stock. The selection is deliberately
// US-only for now; international expansion is a separate effort.
//
// Each entry feeds the page's title, H1, intro, and a brief city
// market blurb. Slugs are city-state hyphenated so URLs are
// unambiguous (Springfield-IL vs Springfield-MA) and Google can
// disambiguate them in the SERP.

export interface City {
  slug: string;       // "austin-tx"
  city: string;       // "Austin"
  state: string;      // "TX"
  stateName: string;  // "Texas"
  // One-sentence market-context blurb. Written so the same blurb works
  // across every trade — focuses on permitting / population / climate
  // angle rather than the specific trade's economics (those go in the
  // trade's own copy).
  marketBlurb: string;
}

export const CITIES: City[] = [
  {
    slug: "austin-tx",
    city: "Austin",
    state: "TX",
    stateName: "Texas",
    marketBlurb:
      "Austin is one of the fastest-growing metros in the country, with new residential permits running at near-record pace across Travis, Williamson, and Hays counties.",
  },
  {
    slug: "dallas-tx",
    city: "Dallas",
    state: "TX",
    stateName: "Texas",
    marketBlurb:
      "The Dallas-Fort Worth metroplex sees more residential construction starts each year than most US states, and Texas summers push HVAC and plumbing service calls into the tens of thousands per month.",
  },
  {
    slug: "houston-tx",
    city: "Houston",
    state: "TX",
    stateName: "Texas",
    marketBlurb:
      "Houston's mix of older housing stock, hurricane-driven repair work, and ongoing new construction across Harris County keeps contractors of every trade booked year-round.",
  },
  {
    slug: "san-antonio-tx",
    city: "San Antonio",
    state: "TX",
    stateName: "Texas",
    marketBlurb:
      "San Antonio's steady growth and the surrounding Bexar County suburbs drive constant demand for new builds, remodels, and ongoing service work.",
  },
  {
    slug: "phoenix-az",
    city: "Phoenix",
    state: "AZ",
    stateName: "Arizona",
    marketBlurb:
      "The Phoenix metro adds tens of thousands of new housing units a year, and 110-degree summers make HVAC, roofing, and plumbing service calls a year-round volume game.",
  },
  {
    slug: "tucson-az",
    city: "Tucson",
    state: "AZ",
    stateName: "Arizona",
    marketBlurb:
      "Tucson's mix of long-term residents and seasonal homeowners drives steady service work, plus a healthy stream of remodel and new-construction permits across Pima County.",
  },
  {
    slug: "denver-co",
    city: "Denver",
    state: "CO",
    stateName: "Colorado",
    marketBlurb:
      "Denver's housing market remains one of the most active in the Rockies, with hail-prone weather driving roofing, gutter, and exterior work in particular.",
  },
  {
    slug: "salt-lake-city-ut",
    city: "Salt Lake City",
    state: "UT",
    stateName: "Utah",
    marketBlurb:
      "Salt Lake's tech-driven population growth keeps residential permits climbing along the Wasatch Front, and ski-belt remodels add a healthy seasonal layer.",
  },
  {
    slug: "las-vegas-nv",
    city: "Las Vegas",
    state: "NV",
    stateName: "Nevada",
    marketBlurb:
      "Las Vegas's heat, dust, and rapid suburban expansion across Clark County keep HVAC, pool, and roofing contractors booked through the summer surge.",
  },
  {
    slug: "atlanta-ga",
    city: "Atlanta",
    state: "GA",
    stateName: "Georgia",
    marketBlurb:
      "Atlanta's sprawling metro and consistent in-migration drive both new-build and remodel volume, while older intown housing fuels steady service work.",
  },
  {
    slug: "charlotte-nc",
    city: "Charlotte",
    state: "NC",
    stateName: "North Carolina",
    marketBlurb:
      "Charlotte and the Mecklenburg County suburbs are among the fastest-growing in the Southeast, with strong permit volume across both single- and multi-family builds.",
  },
  {
    slug: "raleigh-nc",
    city: "Raleigh",
    state: "NC",
    stateName: "North Carolina",
    marketBlurb:
      "The Research Triangle keeps adding tech workers and the housing market keeps responding — Wake County remains one of the most active residential markets in the Carolinas.",
  },
  {
    slug: "nashville-tn",
    city: "Nashville",
    state: "TN",
    stateName: "Tennessee",
    marketBlurb:
      "Nashville's population growth and steady stream of remodels in older Davidson County neighborhoods keep contractors in every trade with full backlogs.",
  },
  {
    slug: "tampa-fl",
    city: "Tampa",
    state: "FL",
    stateName: "Florida",
    marketBlurb:
      "Tampa Bay's mix of new construction, vacation-home renovations, and hurricane-season repair work creates strong demand across every trade.",
  },
  {
    slug: "orlando-fl",
    city: "Orlando",
    state: "FL",
    stateName: "Florida",
    marketBlurb:
      "Orlando's tourism-driven economy and Central Florida's relentless population growth keep both commercial and residential contractors busy year-round.",
  },
  {
    slug: "miami-fl",
    city: "Miami",
    state: "FL",
    stateName: "Florida",
    marketBlurb:
      "Miami-Dade's salt-air corrosion, hurricane code requirements, and year-round build season make for a contractor market that never really cools off.",
  },
  {
    slug: "jacksonville-fl",
    city: "Jacksonville",
    state: "FL",
    stateName: "Florida",
    marketBlurb:
      "Jacksonville's affordable housing and steady population gains drive consistent new-build and remodel work across Duval County.",
  },
  {
    slug: "los-angeles-ca",
    city: "Los Angeles",
    state: "CA",
    stateName: "California",
    marketBlurb:
      "LA's massive housing stock, ADU boom, and constant remodel activity make it one of the country's largest contractor markets — even with permits taking time.",
  },
  {
    slug: "san-diego-ca",
    city: "San Diego",
    state: "CA",
    stateName: "California",
    marketBlurb:
      "San Diego County's mix of coastal premium remodels, military-base housing turnover, and steady new construction keeps contractors at every price point booked.",
  },
  {
    slug: "sacramento-ca",
    city: "Sacramento",
    state: "CA",
    stateName: "California",
    marketBlurb:
      "Sacramento's growth from Bay Area transplants and a strong Central Valley new-build market makes it one of California's most active contractor metros.",
  },
  {
    slug: "seattle-wa",
    city: "Seattle",
    state: "WA",
    stateName: "Washington",
    marketBlurb:
      "Seattle's older housing stock and the King County remodel market keep contractors of every trade busy, especially through the dry summer build season.",
  },
  {
    slug: "portland-or",
    city: "Portland",
    state: "OR",
    stateName: "Oregon",
    marketBlurb:
      "Portland's century-old craftsman housing drives a steady stream of remodel, roofing, and plumbing service work across Multnomah and Washington counties.",
  },
  {
    slug: "chicago-il",
    city: "Chicago",
    state: "IL",
    stateName: "Illinois",
    marketBlurb:
      "Chicago's aging housing stock and brutal winters fuel ongoing service work in HVAC, plumbing, and exterior repairs, while the suburbs add new-build volume.",
  },
  {
    slug: "minneapolis-mn",
    city: "Minneapolis",
    state: "MN",
    stateName: "Minnesota",
    marketBlurb:
      "Minneapolis-St. Paul's harsh winters drive HVAC and roofing work, while the metro's strong economy keeps remodel and new-construction activity steady.",
  },
  {
    slug: "boston-ma",
    city: "Boston",
    state: "MA",
    stateName: "Massachusetts",
    marketBlurb:
      "Boston's mix of historic housing, strict permitting, and high property values makes for one of the highest-revenue contractor markets per job in the country.",
  },
  // ─── Second-tier metros (49 more) — Sun Belt growth + Rust Belt
  // service-call volume + Mountain-West new construction. Same
  // programmatic /{trade}-crm/{city} coverage as
  // the 26 originals. Blurbs stay trade-agnostic per the file's
  // pattern; the trade page adds trade-specific context on top.
  { slug: "fort-worth-tx", city: "Fort Worth", state: "TX", stateName: "Texas",
    marketBlurb: "Fort Worth's population growth across Tarrant County has fueled a decade of steady residential permitting and light-commercial buildout." },
  { slug: "el-paso-tx", city: "El Paso", state: "TX", stateName: "Texas",
    marketBlurb: "El Paso's desert climate and cross-border commerce drive year-round HVAC and roofing demand across the metro." },
  { slug: "arlington-tx", city: "Arlington", state: "TX", stateName: "Texas",
    marketBlurb: "Arlington sits at the heart of the Dallas-Fort Worth metroplex with mixed housing stock and heavy retail/hospitality service demand." },
  { slug: "mesa-az", city: "Mesa", state: "AZ", stateName: "Arizona",
    marketBlurb: "Mesa's Phoenix-metro growth pushes new-construction permitting to the top of the Sun Belt every year." },
  { slug: "scottsdale-az", city: "Scottsdale", state: "AZ", stateName: "Arizona",
    marketBlurb: "Scottsdale's high-end residential remodel market and dense hospitality trade keep specialty contractors booked at premium rates." },
  { slug: "chandler-az", city: "Chandler", state: "AZ", stateName: "Arizona",
    marketBlurb: "Chandler's tech-corridor growth and rapid subdivision buildout drive one of the fastest permit-rate curves in the Southwest." },
  { slug: "tempe-az", city: "Tempe", state: "AZ", stateName: "Arizona",
    marketBlurb: "Tempe's dense student housing + Phoenix-metro service overlap gives a steady service-call baseline year-round." },
  { slug: "aurora-co", city: "Aurora", state: "CO", stateName: "Colorado",
    marketBlurb: "Aurora is Colorado's second-largest city and one of the Denver metro's most active new-construction submarkets." },
  { slug: "colorado-springs-co", city: "Colorado Springs", state: "CO", stateName: "Colorado",
    marketBlurb: "Colorado Springs' military-driven population + Front Range growth keep contractors booked across residential and light-commercial." },
  { slug: "boise-id", city: "Boise", state: "ID", stateName: "Idaho",
    marketBlurb: "Boise leads the Mountain West in per-capita population growth, driving residential permitting to record highs." },
  { slug: "reno-nv", city: "Reno", state: "NV", stateName: "Nevada",
    marketBlurb: "Reno's tech-relocation wave and legacy service-call base combine for one of the healthiest contractor markets in the Great Basin." },
  { slug: "henderson-nv", city: "Henderson", state: "NV", stateName: "Nevada",
    marketBlurb: "Henderson's Las Vegas-metro sprawl fuels ongoing subdivision buildout and long-cycle warranty service demand." },
  { slug: "north-las-vegas-nv", city: "North Las Vegas", state: "NV", stateName: "Nevada",
    marketBlurb: "North Las Vegas has some of the fastest new-home permit rates in the Southwest thanks to Clark County's expansion." },
  { slug: "sacramento-ca", city: "Sacramento", state: "CA", stateName: "California",
    marketBlurb: "Sacramento's Central Valley position + state-capital economy keeps residential and light-commercial contractors booked across mixed housing stock." },
  { slug: "san-francisco-ca", city: "San Francisco", state: "CA", stateName: "California",
    marketBlurb: "San Francisco's high-value remodel market and strict permitting create some of the highest per-job revenue in the country." },
  { slug: "san-jose-ca", city: "San Jose", state: "CA", stateName: "California",
    marketBlurb: "San Jose's tech-driven wealth base keeps residential remodel and specialty-trade demand near the top of the state." },
  { slug: "oakland-ca", city: "Oakland", state: "CA", stateName: "California",
    marketBlurb: "Oakland's mix of Bay Area service demand + older housing stock generates steady remodel and warranty work year-round." },
  { slug: "san-diego-ca", city: "San Diego", state: "CA", stateName: "California",
    marketBlurb: "San Diego's mild climate, coastal salt exposure, and steady tourism economy generate long-cycle service-contract work across trades." },
  { slug: "fresno-ca", city: "Fresno", state: "CA", stateName: "California",
    marketBlurb: "Fresno's Central Valley agriculture + steady residential growth create a service-heavy contractor market with reliable repeat customers." },
  { slug: "long-beach-ca", city: "Long Beach", state: "CA", stateName: "California",
    marketBlurb: "Long Beach's port economy, older housing stock, and dense multifamily inventory keep property-services trades busy year-round." },
  { slug: "portland-or", city: "Portland", state: "OR", stateName: "Oregon",
    marketBlurb: "Portland's stringent green-building requirements and long remodel cycles reward contractors who track projects tightly." },
  { slug: "salem-or", city: "Salem", state: "OR", stateName: "Oregon",
    marketBlurb: "Salem's state-capital stability and Willamette Valley growth create a healthy contractor market with mid-market residential focus." },
  { slug: "seattle-wa", city: "Seattle", state: "WA", stateName: "Washington",
    marketBlurb: "Seattle's tech wealth + wet climate drive both premium remodels and high-volume roofing / drainage service work." },
  { slug: "tacoma-wa", city: "Tacoma", state: "WA", stateName: "Washington",
    marketBlurb: "Tacoma's Puget Sound proximity + military economy keep contractors booked across service and new construction alike." },
  { slug: "spokane-wa", city: "Spokane", state: "WA", stateName: "Washington",
    marketBlurb: "Spokane's Inland Northwest growth and cold-winter HVAC demand generate a resilient service-call baseline." },
  { slug: "albuquerque-nm", city: "Albuquerque", state: "NM", stateName: "New Mexico",
    marketBlurb: "Albuquerque's mix of desert climate and older housing stock drives steady HVAC, roofing, and plumbing service work." },
  { slug: "oklahoma-city-ok", city: "Oklahoma City", state: "OK", stateName: "Oklahoma",
    marketBlurb: "Oklahoma City's steady population growth + tornado-driven roofing rebuild cycle keep contractors of every trade booked." },
  { slug: "tulsa-ok", city: "Tulsa", state: "OK", stateName: "Oklahoma",
    marketBlurb: "Tulsa's energy-economy stability + Green Country residential growth support a diversified contractor market." },
  { slug: "wichita-ks", city: "Wichita", state: "KS", stateName: "Kansas",
    marketBlurb: "Wichita's plains-state weather patterns generate consistent HVAC, roofing, and storm-repair demand across residential and light-commercial." },
  { slug: "kansas-city-mo", city: "Kansas City", state: "MO", stateName: "Missouri",
    marketBlurb: "Kansas City's split-state economy + Midwest freeze-thaw cycles drive year-round service work and steady new construction." },
  { slug: "st-louis-mo", city: "St. Louis", state: "MO", stateName: "Missouri",
    marketBlurb: "St. Louis' older housing stock and Rust Belt remodel demand keep contractors booked with turnover work and long-cycle service." },
  { slug: "memphis-tn", city: "Memphis", state: "TN", stateName: "Tennessee",
    marketBlurb: "Memphis' Delta position + humid climate creates persistent HVAC, drainage, and roofing work across a large residential base." },
  { slug: "knoxville-tn", city: "Knoxville", state: "TN", stateName: "Tennessee",
    marketBlurb: "Knoxville's East Tennessee growth + Smoky Mountains second-home market drive high-mix residential contractor demand." },
  { slug: "chattanooga-tn", city: "Chattanooga", state: "TN", stateName: "Tennessee",
    marketBlurb: "Chattanooga's tech-corridor growth + tourism economy generate steady residential remodel and light-commercial work." },
  { slug: "louisville-ky", city: "Louisville", state: "KY", stateName: "Kentucky",
    marketBlurb: "Louisville's Ohio River economy and older housing stock drive high-mix residential service and remodel demand." },
  { slug: "birmingham-al", city: "Birmingham", state: "AL", stateName: "Alabama",
    marketBlurb: "Birmingham's Deep South growth + humid summers keep HVAC, roofing, and specialty-trades contractors busy year-round." },
  { slug: "raleigh-nc", city: "Raleigh", state: "NC", stateName: "North Carolina",
    marketBlurb: "Raleigh's Research Triangle expansion drives one of the fastest residential permit rates on the East Coast." },
  { slug: "charlotte-nc", city: "Charlotte", state: "NC", stateName: "North Carolina",
    marketBlurb: "Charlotte's financial-services growth and Sun Belt migration fuel steady new construction and long-cycle service work." },
  { slug: "columbia-sc", city: "Columbia", state: "SC", stateName: "South Carolina",
    marketBlurb: "Columbia's state-capital economy and steady growth generate a mid-market residential contractor base with reliable repeat work." },
  { slug: "richmond-va", city: "Richmond", state: "VA", stateName: "Virginia",
    marketBlurb: "Richmond's East Coast corridor position and mid-Atlantic climate drive steady mixed residential and commercial trade work." },
  { slug: "virginia-beach-va", city: "Virginia Beach", state: "VA", stateName: "Virginia",
    marketBlurb: "Virginia Beach's coastal position, military economy, and tourism industry generate high-volume year-round service demand." },
  { slug: "baltimore-md", city: "Baltimore", state: "MD", stateName: "Maryland",
    marketBlurb: "Baltimore's dense older housing stock + Mid-Atlantic freeze-thaw cycle keep contractors booked with repair and turnover work." },
  { slug: "washington-dc", city: "Washington", state: "DC", stateName: "District of Columbia",
    marketBlurb: "Washington DC's strict permitting and high-value remodel market create some of the East Coast's highest per-job revenue." },
  { slug: "detroit-mi", city: "Detroit", state: "MI", stateName: "Michigan",
    marketBlurb: "Detroit's ongoing residential revitalization + harsh winters drive both HVAC service and long-cycle remodel demand." },
  { slug: "grand-rapids-mi", city: "Grand Rapids", state: "MI", stateName: "Michigan",
    marketBlurb: "Grand Rapids' furniture-city legacy + Lake Michigan growth create a healthy mid-market residential contractor base." },
  { slug: "milwaukee-wi", city: "Milwaukee", state: "WI", stateName: "Wisconsin",
    marketBlurb: "Milwaukee's Great Lakes climate + older housing stock keep contractors booked with HVAC, roofing, and remodel work across four seasons." },
  { slug: "madison-wi", city: "Madison", state: "WI", stateName: "Wisconsin",
    marketBlurb: "Madison's state-capital + university economy generates a steady mid-market residential and light-commercial contractor market." },
  { slug: "des-moines-ia", city: "Des Moines", state: "IA", stateName: "Iowa",
    marketBlurb: "Des Moines' Midwest position + severe-weather cycles drive year-round HVAC, roofing, and storm-repair work." },
  { slug: "omaha-ne", city: "Omaha", state: "NE", stateName: "Nebraska",
    marketBlurb: "Omaha's Plains-state economy and freeze-thaw climate maintain steady contractor demand across mixed housing stock." },
];

export const CITY_INDEX = new Map(CITIES.map((c) => [c.slug, c]));
